import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip, Avatar } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import UserDialog from './UserDialog';
import PasswordUpdateDialog from './PasswordUpdateDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const UsersListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [makeAdminLoading, setMakeAdminLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete User ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/User/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'User deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete user. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit User ----------
  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  // ---------- Add User ----------
  const handleAddUser = () => {
    setSelectedUser(null);
    setEditDialogOpen(true);
  };

  // ---------- Update Password ----------
  const handleUpdatePassword = (user: User) => {
    setSelectedUser(user);
    setPasswordDialogOpen(true);
  };

  // ---------- Make Admin ----------
  const handleMakeAdmin = async (user: User) => {
    setMakeAdminLoading(user.id);

    try {
      const response = await DataService.patch(`api/User/make-admin?id=${user.id}`, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `User ${user.name} has been made an admin successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to make user admin');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to make user admin. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setMakeAdminLoading(null);
    }
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (user: User) => {
    setToggleLoading(user.id);

    try {
      const endpoint = user.active
        ? `api/User/deactivate?id=${user.id}`
        : `api/User/activate?id=${user.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `User ${user.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update user status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update user status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedUser ? 'User updated successfully!' : 'User created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Handle Password Update Success ----------
  const handlePasswordUpdateSuccess = () => {
    setPasswordDialogOpen(false);
    dispatch(
      showAlert({
        message: 'Password updated successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Get Role Icon ----------
  const getRoleIcon = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return <AdminPanelSettingsIcon color="error" fontSize="small" />;
      case 'manager':
        return <SupervisorAccountIcon color="warning" fontSize="small" />;
      case 'user':
      default:
        return <AccountCircleIcon color="primary" fontSize="small" />;
    }
  };

  // ---------- Get Role Color ----------
  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      case 'user':
      default:
        return 'primary';
    }
  };

  // ---------- Get User Initials ----------
  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'User',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              bgcolor: 'primary.main'
            }}
          >
            {getUserInitials(params.value)}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {params.value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getRoleIcon(params.value)}
          <Chip
            label={params.value}
            color={getRoleColor(params.value)}
            size="small"
            variant="outlined"
          />
        </Box>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Update Password">
            <IconButton
              size="small"
              onClick={() => handleUpdatePassword(params.row)}
              color="secondary"
            >
              <LockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.role?.toLowerCase() !== 'admin' && (
            <Tooltip title="Make Admin">
              <IconButton
                size="small"
                onClick={() => handleMakeAdmin(params.row)}
                disabled={makeAdminLoading === params.row.id}
                color="warning"
              >
                <SecurityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add User',
      icon: <AddIcon />,
      onClick: handleAddUser,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Users Function ----------
  const fetchUsers = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchUsers called with params:', params);
      try {
        const response = await DataService.get('api/User/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawUsers: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawUsers = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawUsers = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const users: User[] = rawUsers.map(user => ({
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role,
            active: user.active,
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: user.updatedAt || new Date().toISOString()
          }));

          console.log('Raw users from API:', rawUsers);
          console.log('Mapped users:', users);

          // Filter by search term if provided
          let filteredUsers = users;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredUsers = users.filter(
              user =>
                user.name.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower) ||
                user.role.toLowerCase().includes(searchLower)
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredUsers.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof User];
              let bValue: any = b[sortModel.field as keyof User];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedUsers = filteredUsers.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredUsers.length / params.pageSize);

          const result = {
            items: paginatedUsers,
            totalCount: filteredUsers.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load users. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        User Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Users"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchUsers}
        actions={actions}
        emptyMessage="No users found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit User Dialog */}
      <UserDialog
        open={editDialogOpen}
        user={selectedUser}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      {/* Password Update Dialog */}
      <PasswordUpdateDialog
        open={passwordDialogOpen}
        user={selectedUser}
        onClose={() => setPasswordDialogOpen(false)}
        onSuccess={handlePasswordUpdateSuccess}
      />
    </Box>
  );
};

export default UsersListing;